import { gql, useQuery } from '@apollo/client';

import { useCurrent } from '@/hooks/useCurrent';
import { WEBFLOW_PUBLIC_CONFIG_QUERY_DOCUMENT } from '@/lib/webflow-poc';
import type { WebflowNavigationChrome } from '@/lib/webflow-poc/fetch-public-config';
import { normalizeNavigationChrome } from '@/lib/webflow-poc/navigation-chrome';
import {
  normalizeUtilitiesMount,
  type WebflowUtilitiesMount,
  type WebflowUtilitiesMountKind,
} from '@/lib/webflow-poc/webflow-utilities-slot';

type WebflowPublicConfigQuery = {
  webflowIntegrationGetPublicConfig: {
    result: {
      enabled: boolean;
      siteId: string | null;
      publishOrigin: string | null;
      designerUrl: string | null;
      navigationChrome: WebflowNavigationChrome;
      utilitiesMountKind: WebflowUtilitiesMountKind | null;
      utilitiesMountName: string | null;
    };
  };
};

/**
 * Tenant Webflow integration status for client UI.
 *
 * Active only when BOTH:
 * 1. `featureFlags.webflow` is on for the tenant, and
 * 2. Webflow is connected (`enabled` + publish origin on the server).
 *
 * When inactive, GraphQL is skipped and chrome defaults to TRADEWING so
 * Tradewing header/CSS/rules are unchanged.
 */
export const useWebflowPoc = () => {
  const current = useCurrent();
  const featureFlagEnabled = current.tenant?.featureFlags?.webflow === true;

  const { data } = useQuery<WebflowPublicConfigQuery>(
    WEBFLOW_PUBLIC_CONFIG_QUERY_DOCUMENT,
    {
      skip: current.loading || !featureFlagEnabled,
      fetchPolicy: 'cache-first',
    }
  );

  const integrationConfig = data?.webflowIntegrationGetPublicConfig?.result;
  const isEnabled = featureFlagEnabled && integrationConfig?.enabled === true;
  const siteId = isEnabled ? integrationConfig?.siteId || '' : '';
  const designerUrl = isEnabled ? integrationConfig?.designerUrl || '' : '';
  // When Webflow is off / not integrated, force TRADEWING so callers never
  // treat the normalize() default ('WEBFLOW') as active chrome.
  const navigationChrome: WebflowNavigationChrome = isEnabled
    ? normalizeNavigationChrome(integrationConfig?.navigationChrome)
    : 'TRADEWING';
  const utilitiesMount: WebflowUtilitiesMount = isEnabled
    ? normalizeUtilitiesMount(
        integrationConfig?.utilitiesMountKind,
        integrationConfig?.utilitiesMountName
      )
    : normalizeUtilitiesMount(null, null);

  return {
    /** featureFlags.webflow only (integration may still be disconnected). */
    featureFlagEnabled,
    /** Flag on AND integration connected — safe to apply Webflow UI/CSS. */
    isEnabled,
    config: isEnabled
      ? {
          enabled: true,
          siteId,
          designerUrl,
          navigationChrome,
          utilitiesMountKind: utilitiesMount.kind,
          utilitiesMountName: utilitiesMount.name,
        }
      : null,
    designerUrl,
    siteId,
    navigationChrome,
    utilitiesMount,
  };
};

export const WEBFLOW_INITIALIZE_WITH_CODE_MUTATION = gql`
  mutation AdminWebflowIntegrationPage_UseCodeMutation(
    $input: WebflowIntegrationInitializeWithCodeInput!
  ) {
    webflowIntegrationInitializeWithCode(input: $input) {
      status
    }
  }
`;

export const WEBFLOW_BIND_SITE_MUTATION = gql`
  mutation AdminWebflowIntegrationPage_BindSiteMutation(
    $input: WebflowIntegrationBindSiteInput!
  ) {
    webflowIntegrationBindSite(input: $input) {
      status
    }
  }
`;

export const WEBFLOW_UPSERT_SITE_CONFIG_MUTATION = gql`
  mutation AdminWebflowIntegrationPage_UpsertSiteConfigMutation(
    $input: WebflowIntegrationUpsertSiteConfigInput!
  ) {
    webflowIntegrationUpsertSiteConfig(input: $input) {
      status
    }
  }
`;

export const WEBFLOW_UPDATE_NAVIGATION_CHROME_MUTATION = gql`
  mutation AdminWebflowIntegrationPage_UpdateNavigationChromeMutation(
    $input: WebflowIntegrationUpdateNavigationChromeInput!
  ) {
    webflowIntegrationUpdateNavigationChrome(input: $input) {
      status
      navigationChrome
    }
  }
`;

export const WEBFLOW_UPDATE_UTILITIES_MOUNT_MUTATION = gql`
  mutation AdminWebflowIntegrationPage_UpdateUtilitiesMountMutation(
    $input: WebflowIntegrationUpdateUtilitiesMountInput!
  ) {
    webflowIntegrationUpdateUtilitiesMount(input: $input) {
      status
      utilitiesMountKind
      utilitiesMountName
    }
  }
`;

export const WEBFLOW_RESET_CONNECTION_MUTATION = gql`
  mutation AdminWebflowIntegrationPage_ResetConnectionMutation {
    webflowIntegrationResetConnection {
      status
    }
  }
`;

export const WEBFLOW_DESTROY_INTEGRATION_MUTATION = gql`
  mutation AdminWebflowIntegrationPage_DestroyIntegrationMutation {
    webflowIntegrationDestroyIntegration {
      status
    }
  }
`;
